import React, { useEffect, useRef, useState } from 'react';
import { config } from './config';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { useFetchData } from '../../hooks/useFetchData';
import { useDispatch, useSelector } from 'react-redux';
import { accountState, AppDispatch, RootState } from '../../store';
import { ALL_VALUE } from '../Invoice/config';
import { format } from 'date-fns';
import './accountWiseBreakup.css';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import CollapsibleTable from '../../components/MasterComponent/CollapsibleTable/CollapsibleTable';
import MonthlyBillingView from './MonthlyBillingView';
import BillingHeader from '../../components/BillingConsole/BillingHeader';
import { setSelectedMonth } from '../../store/monthSlice';

const AccountWiseBreakup: React.FC = () => {
  const selectedAccountInfo = useSelector(accountState);
  type ViewMode =
    | 'accountview-monthly'
    | 'accountview-previousmonth'
    | 'accountview-daily';

  const getLastMonths = (count = 10) => {
    const months = [];
    const today = new Date();

    for (let i = 0; i < count; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      months.push(`${monthName} ${date.getFullYear()}`);
    }

    return months;
  };

  type ServiceItem = {
    name: string;
    rate: string;
    usage: string;
    amount: string;
  };

  type Region = {
    regionName: string;
    items: ServiceItem[];
  };

  type ServiceData = {
    serviceName: string;
    total: string;
    regions: Region[];
  };

  type AccountData = {
    accountId: string;
    accountName: string;
    total: string;
    services: ServiceData[];
  };

  // Define view modes configuration for AccountWiseBreakup
  const viewModes = [
    {
      key: 'accountview-monthly',
      label: 'Monthly View',
      buttonText: 'Monthly View',
    },
    {
      key: 'accountview-previousmonth',
      label: 'Previous Months',
      buttonText: 'Previous Months',
    },
    {
      key: 'accountview-daily',
      label: 'Daily Breakup',
      buttonText: 'Daily Breakup',
    },
  ];

  const [viewMode, setViewMode] = useState<ViewMode>('accountview-monthly');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const selectedMonth = useSelector((state: RootState) => state.month.selectedMonth);
  const monthOptions = useSelector((state: RootState) => state.month.monthOptions);
  const monthValue = selectedMonth || (monthOptions[0] ?? '');
  const [selectedAccountId, setSelectedAccountId] = React.useState<any>(
    selectedAccountInfo.selectedAccountId
  );

  const [accountsData, setAccountsData] = useState<AccountData[]>([]);
  const [openAccounts, setOpenAccounts] = useState<Record<string, boolean>>({});
  const [openRegions, setOpenRegions] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const reqBodyRef = useRef({
    view: viewMode,
    month: format(new Date(selectedMonth), 'yyyy-MM-01'),
    formate: 'detailed',
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getAccountWiseBreakupReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  const toggleAccount = (accountName: string) => {
    setOpenAccounts((prev) => ({
      ...prev,
      [accountName]: !prev[accountName],
    }));
  };

  const handleToggleAll = () => {
    const expand = !isExpanded;
    setIsExpanded(expand);

    const allAccounts = accountsData.reduce((acc, account) => {
      acc[account.accountId] = expand;

      // Also expand all account-service combos
      account.services.forEach((service) => {
        acc[`${account.accountId}-${service.serviceName}`] = expand;
      });

      return acc;
    }, {} as Record<string, boolean>);
    setOpenAccounts(allAccounts);

    const allRegions = accountsData.reduce((regionAcc, account) => {
      account.services.forEach((service) => {
        const key = `${account.accountId}-${service.serviceName}`;
        regionAcc[key] = service.regions.reduce((innerAcc, region) => {
          innerAcc[region.regionName] = expand;
          return innerAcc;
        }, {} as Record<string, boolean>);
      });
      return regionAcc;
    }, {} as Record<string, Record<string, boolean>>);

    setOpenRegions(allRegions);
  };

  const handleViewModeChange = (newViewMode: string) => {
    setViewMode(newViewMode as ViewMode);
  };

  // account selection section
  const onAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS)
      setSelectedAccountId(data.accountId);
  };

  const getCurrentViewLabel = () => {
    const currentView = viewModes.find((vm) => vm.key === viewMode);
    return currentView ? currentView.label : 'Monthly View';
  };

  useEffect(() => {
    reqBodyRef.current = {
      view: viewMode,
      month: format(new Date(selectedMonth), 'yyyy-MM-01'),
      formate: 'detailed',
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };

    //  Reset expanded states before fetching
    setOpenAccounts({});
    setOpenRegions({});
    setIsExpanded(false);

    fetch();
  }, [viewMode, selectedAccountId, selectedMonth]);

  let isCanceled = false;

  useEffect(() => {
    if (data && !isSubmitting && !isCanceled) {
      setAccountsData(data);
    }

    return () => {
      isCanceled = true;
    };
  }, [data, isSubmitting]);

  return (
    <div className="app-container">
      <TitleHeader
        extraContent={
          <BillingHeader
            viewModes={viewModes}
            currentViewMode={viewMode}
            selectedMonth={monthValue}
            onViewModeChange={handleViewModeChange}
            onMonthChange={(e) => dispatch(setSelectedMonth(e.target.value))}
            onAccountChange={onAccountChange}
            isSubmitting={isSubmitting}
          />
        }
      >
        {getCurrentViewLabel()}
      </TitleHeader>

      {viewMode === 'accountview-monthly' && (
        <MonthlyBillingView
          accountsData={accountsData ?? []}
          isLoading={isSubmitting}
          isExpanded={isExpanded}
          openAccounts={openAccounts}
          openRegions={openRegions}
          onToggleAll={handleToggleAll}
          onToggleAccount={toggleAccount}
          setOpenRegions={setOpenRegions}
        />
      )}

      {viewMode === 'accountview-previousmonth' && (
        <CollapsibleTable
          data={accountsData}
          isLoading={isSubmitting}
          className="customTableStyle"
        />
      )}

      {viewMode === 'accountview-daily' && (
        <CollapsibleTable
          data={accountsData}
          isLoading={isSubmitting}
          className="customTableStyle"
        />
      )}
    </div>
  );
};

export default AccountWiseBreakup;
